import React, { useEffect } from 'react';
import { UseFormRegister, UseFormWatch, UseFormSetValue, FieldErrors } from 'react-hook-form';
import { Icon } from '@/shared/icons'
import type { IconName } from '@/shared/icons'
import { UploadInput } from '@/shared/components/ui/UploadInput';
interface GatewayFormProps {
  register: UseFormRegister<any>;
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
  errors: FieldErrors<any>;
}

const MESSAGE_TYPES = [
  { value: 'text', label: 'Texto', icon: 'message-square', description: 'Mensagem de texto simples' },
  { value: 'image', label: 'Imagem', icon: 'image', description: 'Enviar imagem com legenda' },
  { value: 'video', label: 'Vídeo', icon: 'video', description: 'Enviar vídeo com legenda' },
  { value: 'audio', label: 'Áudio', icon: 'music', description: 'Enviar arquivo de áudio' },
  { value: 'document', label: 'Documento', icon: 'file', description: 'Enviar PDF, DOC, etc.' },
  { value: 'location', label: 'Localização', icon: 'map-pin', description: 'Compartilhar localização' },
  { value: 'contact', label: 'Contato', icon: 'user', description: 'Compartilhar vCard' },
];

// Filtro do seletor de arquivo por tipo de mídia (o backend valida o mimetype real)
const ACCEPT_MAP: Record<string, string> = {
  image: 'image/*',
  video: 'video/*',
  audio: 'audio/*',
  document: '*/*',
};

export const GatewayForm: React.FC<GatewayFormProps> = ({ register, watch, setValue, errors }) => {
  const messageType = watch('providerConfig.messageData.type') || 'text';

  // ✅ CRITICAL: Inicializar valores padrão obrigatórios na montagem
  useEffect(() => {
    setValue('providerConfig.providerType', 'whatsapp_gateway');

    // Inicializar messageData.type se não existir
    const messageData = watch('providerConfig.messageData');
    if (!messageData || !messageData.type) {
      setValue('providerConfig.messageData.type', 'text', { shouldValidate: false });
    }
  }, []);  // Roda apenas uma vez na montagem

  // ✅ CRITICAL: Garantir que type sempre exista quando messageData mudar
  const currentMessageData = watch('providerConfig.messageData');
  useEffect(() => {
    if (currentMessageData && !currentMessageData.type) {
      setValue('providerConfig.messageData.type', 'text', { shouldValidate: false });
    }
  }, [currentMessageData, setValue]);

  // ✅ CRITICAL: Garantir que os inputs sejam renderizados quando os dados forem carregados
  // (Forçar re-render quando messageData mudar no modo de edição)
  const existingMessageData = watch('providerConfig.messageData');
  useEffect(() => {
    // Este useEffect garante que o componente re-renderize quando os dados forem carregados
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(existingMessageData)]);

  const handleTypeChange = (type: string) => {
    setValue('providerConfig.messageData.type', type);
    // Reset type-specific fields
    setValue('providerConfig.messageData.mediaUrl', undefined);
    setValue('providerConfig.messageData.caption', undefined);
    setValue('providerConfig.messageData.filename', undefined);
    setValue('providerConfig.messageData.location', undefined);
    setValue('providerConfig.messageData.contact', undefined);
  };

  return (
    <div className="space-y-6">
      {/* Message Type Selection */}
      <div>
        <label className="block text-sm font-medium mb-3" style={{ color: 'var(--theme-card-text)' }}>
          Tipo de Mensagem <span style={{ color: 'var(--theme-error-color)' }}>*</span>
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {MESSAGE_TYPES.map(({ value, label, icon, description }) => (
            <button
              key={value}
              type="button"
              onClick={() => handleTypeChange(value)}
              className="p-4 rounded-lg border-2 transition-all text-left"
              style={{
                borderColor: messageType === value ? 'var(--theme-primary)' : 'var(--theme-input-border)',
                backgroundColor: messageType === value ? 'var(--theme-primary-background)' : 'var(--theme-input-background)',
              }}
            >
              <Icon
                name={icon as IconName}
                className="w-6 h-6 mb-2"
                style={{ color: messageType === value ? 'var(--theme-primary)' : 'var(--theme-muted-text)' }}
              />
              <p className="text-sm font-medium mb-0.5" style={{ color: 'var(--theme-card-text)' }}>
                {label}
              </p>
              <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                {description}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Text Message */}
      {messageType === 'text' && (
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-card-text)' }}>
            Mensagem <span style={{ color: 'var(--theme-error-color)' }}>*</span>
          </label>
          <textarea
            {...register('providerConfig.messageData.message')}
            placeholder="Digite a mensagem. Use {{1}}, {{2}}, etc. para variáveis"
            rows={6}
            className="w-full px-4 py-3 rounded-lg border text-sm resize-none"
            style={{
              backgroundColor: 'var(--theme-input-background)',
              borderColor: 'var(--theme-input-border)',
              color: 'var(--theme-input-text)',
            }}
          />
          <div className="flex items-center gap-2 mt-2">
            <Icon name="alert-circle" className="w-4 h-4" style={{ color: 'var(--theme-info-color)' }} />
            <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
              Use variáveis numéricas: {'{'}
              {'{'}1{'}}'}
              {'}'}, {'{'}
              {'{'}2{'}}'}
              {'}'}, {'{'}
              {'{'}3{'}}'}
              {'}'}... para personalizar a mensagem
            </p>
          </div>
          {(errors.providerConfig as any)?.messageData?.message && (
            <p className="text-sm mt-1" style={{ color: 'var(--theme-error-color)' }}>
              {((errors.providerConfig as any).messageData as any).message.message as string}
            </p>
          )}
        </div>
      )}

      {/* Media Messages (image, video, audio, document) */}
      {['image', 'video', 'audio', 'document'].includes(messageType) && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-card-text)' }}>
              URL da Mídia <span style={{ color: 'var(--theme-error-color)' }}>*</span>
            </label>
            <UploadInput
              value={watch('providerConfig.messageData.mediaUrl') || ''}
              onChange={(url) =>
                setValue('providerConfig.messageData.mediaUrl', url, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              accept={ACCEPT_MAP[messageType]}
              folder="templates"
              placeholder="https://exemplo.com/arquivo.jpg"
            />
            <p className="text-xs mt-1.5" style={{ color: 'var(--theme-muted-text)' }}>
              URL pública do arquivo. Use variáveis como {'{'}
              {'{'}1{'}}'}
              {'}'} para URLs dinâmicas
            </p>
            {(errors.providerConfig as any)?.messageData?.mediaUrl && (
              <p className="text-sm mt-1" style={{ color: 'var(--theme-error-color)' }}>
                {((errors.providerConfig as any).messageData as any).mediaUrl.message as string}
              </p>
            )}
          </div>

          {messageType === 'document' && (
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-card-text)' }}>
                Nome do Arquivo (opcional)
              </label>
              <input
                type="text"
                {...register('providerConfig.messageData.filename')}
                placeholder="documento.pdf"
                className="w-full px-4 py-2 rounded-lg border text-sm h-[38px]"
                style={{
                  backgroundColor: 'var(--theme-input-background)',
                  borderColor: 'var(--theme-input-border)',
                  color: 'var(--theme-input-text)',
                }}
              />
            </div>
          )}

          {['image', 'video'].includes(messageType) && (
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-card-text)' }}>
                Legenda (opcional)
              </label>
              <textarea
                {...register('providerConfig.messageData.caption')}
                placeholder="Digite a legenda. Use {{1}}, {{2}}, etc. para variáveis"
                rows={3}
                className="w-full px-4 py-2 rounded-lg border text-sm resize-none"
                style={{
                  backgroundColor: 'var(--theme-input-background)',
                  borderColor: 'var(--theme-input-border)',
                  color: 'var(--theme-input-text)',
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* Location Message */}
      {messageType === 'location' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-card-text)' }}>
                Latitude <span style={{ color: 'var(--theme-error-color)' }}>*</span>
              </label>
              <input
                type="text"
                {...register('providerConfig.messageData.location.latitude')}
                placeholder="-23.550520"
                className="w-full px-4 py-2 rounded-lg border text-sm h-[38px]"
                style={{
                  backgroundColor: 'var(--theme-input-background)',
                  borderColor: 'var(--theme-input-border)',
                  color: 'var(--theme-input-text)',
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-card-text)' }}>
                Longitude <span style={{ color: 'var(--theme-error-color)' }}>*</span>
              </label>
              <input
                type="text"
                {...register('providerConfig.messageData.location.longitude')}
                placeholder="-46.633308"
                className="w-full px-4 py-2 rounded-lg border text-sm h-[38px]"
                style={{
                  backgroundColor: 'var(--theme-input-background)',
                  borderColor: 'var(--theme-input-border)',
                  color: 'var(--theme-input-text)',
                }}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-card-text)' }}>
              Nome do Local (opcional)
            </label>
            <input
              type="text"
              {...register('providerConfig.messageData.location.name')}
              placeholder="Av. Paulista, 1578"
              className="w-full px-4 py-2 rounded-lg border text-sm h-[38px]"
              style={{
                backgroundColor: 'var(--theme-input-background)',
                borderColor: 'var(--theme-input-border)',
                color: 'var(--theme-input-text)',
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-card-text)' }}>
              Endereço (opcional)
            </label>
            <input
              type="text"
              {...register('providerConfig.messageData.location.address')}
              placeholder="São Paulo, SP, Brasil"
              className="w-full px-4 py-2 rounded-lg border text-sm h-[38px]"
              style={{
                backgroundColor: 'var(--theme-input-background)',
                borderColor: 'var(--theme-input-border)',
                color: 'var(--theme-input-text)',
              }}
            />
          </div>
          <div className="flex items-start gap-2 p-3 rounded-lg" style={{ backgroundColor: 'var(--theme-info-background)' }}>
            <Icon name="alert-circle" className="w-4 h-4 mt-0.5" style={{ color: 'var(--theme-info-color)' }} />
            <p className="text-xs" style={{ color: 'var(--theme-card-text)' }}>
              Você pode usar variáveis {'{'}
              {'{'}1{'}}'}
              {'}'} para latitude, {'{'}
              {'{'}2{'}}'}
              {'}'} para longitude, etc. Isso permite enviar localizações dinâmicas.
            </p>
          </div>
        </div>
      )}

      {/* Contact Message */}
      {messageType === 'contact' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-card-text)' }}>
              Nome do Contato <span style={{ color: 'var(--theme-error-color)' }}>*</span>
            </label>
            <input
              type="text"
              {...register('providerConfig.messageData.contact.name')}
              placeholder="João Silva"
              className="w-full px-4 py-2 rounded-lg border text-sm h-[38px]"
              style={{
                backgroundColor: 'var(--theme-input-background)',
                borderColor: 'var(--theme-input-border)',
                color: 'var(--theme-input-text)',
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-card-text)' }}>
              Telefone (opcional)
            </label>
            <input
              type="text"
              {...register('providerConfig.messageData.contact.phone')}
              placeholder="+55 11 98765-4321"
              className="w-full px-4 py-2 rounded-lg border text-sm h-[38px]"
              style={{
                backgroundColor: 'var(--theme-input-background)',
                borderColor: 'var(--theme-input-border)',
                color: 'var(--theme-input-text)',
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-card-text)' }}>
              Email (opcional)
            </label>
            <input
              type="email"
              {...register('providerConfig.messageData.contact.email')}
              placeholder="joao@exemplo.com"
              className="w-full px-4 py-2 rounded-lg border text-sm h-[38px]"
              style={{
                backgroundColor: 'var(--theme-input-background)',
                borderColor: 'var(--theme-input-border)',
                color: 'var(--theme-input-text)',
              }}
            />
          </div>
          <div className="flex items-start gap-2 p-3 rounded-lg" style={{ backgroundColor: 'var(--theme-info-background)' }}>
            <Icon name="alert-circle" className="w-4 h-4 mt-0.5" style={{ color: 'var(--theme-info-color)' }} />
            <p className="text-xs" style={{ color: 'var(--theme-card-text)' }}>
              Use variáveis como {'{'}
              {'{'}1{'}}'}
              {'}'} para nome, {'{'}
              {'{'}2{'}}'}
              {'}'} para telefone, etc. O vCard será gerado automaticamente.
            </p>
          </div>
        </div>
      )}

      {/* General Info */}
      <div className="rounded-lg border p-4" style={{ borderColor: 'var(--theme-card-border)', backgroundColor: 'var(--theme-card-background)' }}>
        <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--theme-card-text)' }}>
          💡 Sobre Templates Gateway
        </h4>
        <ul className="space-y-1.5 text-xs" style={{ color: 'var(--theme-muted-text)' }}>
          <li>• Templates Gateway não precisam de aprovação do Meta</li>
          <li>• Envio imediato após criação do template</li>
          <li>• Suporta todos os tipos de mídia do WhatsApp</li>
          <li>• Use variáveis numéricas {'{'}
            {'{'}1{'}}'}
            {'}'}, {'{'}
            {'{'}2{'}}'}
            {'}'}, {'{'}
            {'{'}3{'}}'}
            {'}'} para personalização</li>
          <li>• Ideal para mensagens transacionais e notificações</li>
        </ul>
      </div>
    </div>
  );
};
